import SyncButton from '@/components/SyncButton';
import findItemByKey from '@/helpers/findItemByKey';
import findSelectedKeys from '@/helpers/findSelectedKeys';
import { User } from '@/redux/auth/authTypes';
import { Avatar, Layout, Menu, Typography } from 'antd';
import { MenuProps } from 'antd/lib';
import { useCallback } from 'react';
import { FaMobile, FaStore } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import classes from './SidebarLayout.module.css';

const { Sider } = Layout;
const { Text } = Typography;

interface SidebarLayoutProps {
  items: any[];
  user: User | null;
}

const SidebarLayout = ({ items, user }: SidebarLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const selectedKeys = findSelectedKeys(location.pathname, items);

  const handleMenuItemSelect: MenuProps['onSelect'] = useCallback(
    (e: any) => {
      const selectedItem = findItemByKey(e.key, items);

      if (!selectedItem) console.error('selectedItem not found');

      // navigate to the selected item url
      if (selectedItem?.url) {
        navigate(selectedItem.url);
      }
    },
    [items, navigate]
  );

  // Get the first letter of user's name for avatar
  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : '?';

  return (
    <Sider
      className={classes.sidebar}
      theme="light"
      width={250}
      style={{
        backgroundColor: 'var(--bg-clr)',
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* User Profile Section */}
      <div
        className="p-3 bg-cover bg-center relative"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url(/assets/img/login.jpeg)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <div className="flex items-center gap-3 mb-3">
          <Avatar
            size={44}
            style={{
              backgroundColor: '#1890ff',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 'bold',
            }}
          >
            {userInitial}
          </Avatar>
          <div className="flex flex-col">
            <Text strong className="text-white text-lg">
              {user?.name}
            </Text>
            <Text className="text-white text-opacity-80 text-sm">Cashier</Text>
          </div>
        </div>

        <div className="mt-3 bg-white bg-opacity-10 rounded-lg p-2.5">
          <div className="flex items-center gap-2 mb-1.5">
            <FaStore size={14} className="text-white opacity-70" />
            <Text className="text-white text-sm font-medium truncate">
              {user?.activeStore?.name}
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <FaMobile size={14} className="text-white opacity-70" />
            <Text className="text-white text-sm font-medium truncate">{user?.devicePOS?.name}</Text>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <div className="flex-1 overflow-auto">
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          className="bg-background dark:bg-background-dark border-0 py-2"
          items={items.map((item) => ({ ...item, className: 'menuItem' }))}
          onSelect={handleMenuItemSelect}
        />
      </div>

      {/* Sync Button Section */}
      <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-background dark:bg-background-dark">
        <SyncButton />
      </div>
    </Sider>
  );
};

export default SidebarLayout;
